import {Box, Tab, Tabs} from '@mui/material';
import {t} from 'i18next';
import './IngestionAnalyticsTabs.scss';
import {useEffect, useState} from 'react';
import EsIndexing from '../es-indexing/EsIndexing';
import Analytics from '../analytics/Analytics';
import ExcelProcessing from '../excel-processing/ExcelProcessing';
import IndexManagement from '../index-management/IndexManagement';
import {INGESTION_ANALYTICS_TAB} from '../analyticsTabConstants';
import {
  setTabValue,
  resetCustomIndexState,
} from '../../../redux/slice/createCustomIndexSlice';
import {useESIndexingConfigInfo} from '../../../hooks/ingestions/useIngestions';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../shared/constants';

interface IngestionAnalyticsTabsProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  className?: string;
}

interface TabData {
  value: number;
  label: string;
  content: React.ReactNode;
}

function TabPanel(props: Readonly<IngestionAnalyticsTabsProps>) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      data-testid={`ingestion-analytics-tabs-${index}`}
    >
      {value === index && (
        <div data-testid={`ingestion-analytics-tabs-child-${index}`}>
          {children}
        </div>
      )}
    </div>
  );
}

const IngestionAnalyticsTabs = () => {
  const dispatch = useDispatch();
  const {tabValue} = useSelector((state: RootState) => state.indexManagement);
  const hasNewIndex = useSelector(
    (state: RootState) => state.indexManagement?.newIndex || false
  );
  const hasNewIndexDetails = useSelector(
    (state: RootState) =>
      state.indexManagement?.newIndexDetails?.responseMessage || ''
  );
  const {data: esIndexingConfig, isLoading: isESIndexingConfigLoading} =
    useESIndexingConfigInfo();
  const isExcelProcessingTabEnabled =
    esIndexingConfig?.excelTabProcessingEnabled === true;
  const [value, setValue] = useState(tabValue);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    dispatch(setTabValue(newValue));
  };
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    setValue(tabValue);
  }, [tabValue]);

  useEffect(() => {
    if (hasNewIndex) {
      setToastMessage(hasNewIndexDetails);
      setValue(INGESTION_ANALYTICS_TAB.INDEX_MANAGEMENT);
      setShowToast(true);
      dispatch(resetCustomIndexState());
    }
  }, [hasNewIndex, hasNewIndexDetails, dispatch]);

  useEffect(() => {
    if (
      !isESIndexingConfigLoading &&
      !isExcelProcessingTabEnabled &&
      tabValue === INGESTION_ANALYTICS_TAB.EXCEL_PROCESSING
    ) {
      dispatch(setTabValue(INGESTION_ANALYTICS_TAB.ANALYTICS));
    }
  }, [
    dispatch,
    isESIndexingConfigLoading,
    isExcelProcessingTabEnabled,
    tabValue,
  ]);

  const tabData: TabData[] = [
    {
      value: INGESTION_ANALYTICS_TAB.ES_INDEXING,
      label: t('ingestions.ingestionAnalytics.tabs.labelEsIndexing'),
      content: <EsIndexing />,
    },
    {
      value: INGESTION_ANALYTICS_TAB.ANALYTICS,
      label: t('ingestions.ingestionAnalytics.tabs.labelAnalytics'),
      content: <Analytics />,
    },
    ...(isExcelProcessingTabEnabled
      ? [
          {
            value: INGESTION_ANALYTICS_TAB.EXCEL_PROCESSING,
            label: t('ingestions.ingestionAnalytics.tabs.labelExcelProcessing'),
            content: <ExcelProcessing />,
          },
        ]
      : []),
    {
      value: INGESTION_ANALYTICS_TAB.INDEX_MANAGEMENT,
      label: t('ingestions.ingestionAnalytics.tabs.labelIndexManagement'),
      content: <IndexManagement />,
    },
  ];

  function a11yProps(tabValue: number) {
    return {
      id: `simple-tab-${tabValue}`,
      'aria-controls': `simple-tabpanel-${tabValue}`,
    };
  }
  return (
    <Box className="analytics-content">
      <Box className="tabs-section" data-testid="tabs-section">
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="Analytics tabs"
          className="tabs-content"
          data-testid="tabs"
        >
          {tabData.map((tab) => (
            <Tab
              key={tab.label}
              value={tab.value}
              label={tab.label}
              className="tab-content"
              data-testid={`tab-${tab.value}`}
              {...a11yProps(tab.value)}
            />
          ))}
        </Tabs>
      </Box>
      <Box className="tabs-child">
        {tabData.map((tab) => (
          <TabPanel
            key={tab.label}
            value={value}
            index={tab.value}
            data-testid={`tabpanel-${tab.value}`}
          >
            {tab.content}
          </TabPanel>
        ))}
      </Box>
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={[ToastType.Success]}
          onClose={() => setShowToast(false)}
        />
      )}
    </Box>
  );
};

export default IngestionAnalyticsTabs;
